import React, { useState } from 'react';
import { speakText } from '../utils/speak';
import { useLanguage } from '../i18n/LanguageContext';
import { useUserLanguage } from '../i18n/UserLanguageContext';
import './FlashcardSession.css';

const TEXT = {
    en: { empty: 'No words to practice with this filter yet.', close: 'Close', reveal: 'Tap to reveal', hear: '🔊 Hear sentence', hearTitle: 'Hear sentence', learning: 'Still learning', knew: 'I knew it', hint: 'Tap the card to see the answer', done: 'Session complete!', back: 'Back to word bank' },
    it: { empty: 'Ancora nessuna parola da esercitare con questo filtro.', close: 'Chiudi', reveal: 'Tocca per rivelare', hear: '🔊 Ascolta la frase', hearTitle: 'Ascolta la frase', learning: 'Ancora da imparare', knew: 'Lo sapevo', hint: 'Tocca la carta per vedere la risposta', done: 'Sessione completata!', back: 'Torna al Word Bank' },
    es: { empty: 'Todavía no hay palabras para practicar con este filtro.', close: 'Cerrar', reveal: 'Toca para ver', hear: '🔊 Escuchar la frase', hearTitle: 'Escuchar la frase', learning: 'Aún aprendiendo', knew: 'Ya la sabía', hint: 'Toca la tarjeta para ver la respuesta', done: '¡Sesión completada!', back: 'Volver al Word Bank' },
    fr: { empty: 'Aucun mot à réviser avec ce filtre pour l’instant.', close: 'Fermer', reveal: 'Touche pour révéler', hear: '🔊 Écouter la phrase', hearTitle: 'Écouter la phrase', learning: 'Encore à apprendre', knew: 'Je le savais', hint: 'Touche la carte pour voir la réponse', done: 'Séance terminée !', back: 'Retour au Word Bank' },
    pt: { empty: 'Ainda não há palavras para praticar com este filtro.', close: 'Fechar', reveal: 'Toque para revelar', hear: '🔊 Ouvir a frase', hearTitle: 'Ouvir a frase', learning: 'Ainda aprendendo', knew: 'Eu sabia', hint: 'Toque no cartão para ver a resposta', done: 'Sessão concluída!', back: 'Voltar ao Word Bank' },
};

function FlashcardSession({ words, onAnswer, onClose }) {
    const { interfaceLanguage } = useLanguage();
    const { activeLanguage } = useUserLanguage();
    const uiLang = TEXT[interfaceLanguage] ? interfaceLanguage : 'en';
    const s = (key) => TEXT[uiLang][key] || TEXT.en[key];
    const targetLang = (activeLanguage || localStorage.getItem('vozoly_target_language') || 'italian').toLowerCase();
    const [index, setIndex] = useState(0);
    const [flipped, setFlipped] = useState(false);
    const [knewCount, setKnewCount] = useState(0);
    const [learningCount, setLearningCount] = useState(0);

    if (words.length === 0) {
        return (
            <div className="fc-overlay">
                <div className="fc-session">
                    <div className="fc-empty">{s('empty')}</div>
                    <button className="fc-close-btn" onClick={onClose}>{s('close')}</button>
                </div>
            </div>
        );
    }

    const isDone = index >= words.length;
    const word = !isDone ? words[index] : null;

    const handleAnswer = (knew) => {
        onAnswer(word.id, knew);
        if (knew) setKnewCount(c => c + 1); else setLearningCount(c => c + 1);
        setFlipped(false);
        setIndex(i => i + 1);
    };

    return (
        <div className="fc-overlay">
            <div className="fc-session">
                <button className="fc-close" onClick={onClose}>✕</button>

                {!isDone ? (
                    <>
                        <div className="fc-progress">{index + 1} / {words.length}</div>
                        <div className={`fc-flashcard ${flipped ? 'flipped' : ''}`} onClick={() => setFlipped(f => !f)}>
                            <div className="fc-flashcard-inner">
                                <div className="fc-flashcard-front">
                                    <div className="fc-word">{word.word}</div>
                                    <div className="fc-tap-hint">{s('reveal')}</div>
                                </div>
                                <div className="fc-flashcard-back">
                                    <div className="fc-translation">{word.translation || '—'}</div>
                                    {word.phonetic && <div className="fc-phonetic">{word.phonetic}</div>}
                                    {word.note && <div className="fc-note">{word.note}</div>}
                                    {word.examples && word.examples.length > 0 && (
                                        <div className="fc-example">
                                            <div className="fc-example-it">{word.examples[0].it}</div>
                                            <div className="fc-example-en">{word.examples[0].en}</div>
                                            <button
                                                className="fc-speak-btn"
                                                onClick={(e) => { e.stopPropagation(); speakText(word.examples[0].it, undefined, targetLang); }}
                                                title={s('hearTitle')}
                                            >
                                                {s('hear')}
                                            </button>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        {flipped ? (
                            <div className="fc-answer-row">
                                <button className="fc-answer-btn learning" onClick={() => handleAnswer(false)}>
                                    {s('learning')}
                                </button>
                                <button className="fc-answer-btn knew" onClick={() => handleAnswer(true)}>
                                    {s('knew')}
                                </button>
                            </div>
                        ) : (
                            <div className="fc-hint-text">{s('hint')}</div>
                        )}
                    </>
                ) : (
                    <div className="fc-summary">
                        <div className="fc-summary-icon">🎉</div>
                        <div className="fc-summary-title">{s('done')}</div>
                        <div className="fc-summary-stats">
                            <div className="fc-summary-stat">
                                <div className="fc-summary-value green">{knewCount}</div>
                                <div className="fc-summary-label">{s('knew')}</div>
                            </div>
                            <div className="fc-summary-stat">
                                <div className="fc-summary-value amber">{learningCount}</div>
                                <div className="fc-summary-label">{s('learning')}</div>
                            </div>
                        </div>
                        <button className="fc-close-btn" onClick={onClose}>{s('back')}</button>
                    </div>
                )}
            </div>
        </div>
    );
}

export default FlashcardSession;